import React from "react";
import { CheckSquare, AlertCircle, HelpCircle, RotateCcw } from "lucide-react";
import type { SowFields } from "../../../../../utils/api/endpoints/sow";

interface DependenciesAssumptionsEditorProps {
  fields: SowFields;
  setField: <K extends keyof SowFields>(key: K, value: SowFields[K]) => void;
}

const defaultAssumptions = `The platform is fully publicly available for the duration of the testing window.
Testing is performed against the production environment, as directed by the Client.
Workflows available to the standard access tier direct outbound communications to simulated recipients only, so testing of that tier will not generate live communications to third parties.
Identity-verification functionality is exercised only against the Client's non-billable sandbox path; CTB will not perform identity verification against live or billable services.
The page count, functional inventory, API endpoint inventory, and the services hosted on the authorised external network address have not been confirmed and will be established during scope validation. Representative sampling may be applied where functionality and security controls are substantially identical.
No source code, secure code review, or architecture review is included.`;

const defaultClientDependencies = `Confirm the date on which the platform becomes publicly available, so the testing window can be scheduled.
Provision and maintain test accounts for the standard and privileged access tiers, release privileged credentials following execution of this Statement of Work, and provision additional accounts on request where required to complete the agreed coverage.
Allowlist CTB's testing source address and confirm any required adjustments to web application firewall, bot protection, or rate-limiting controls.
Ensure the payment path is testable end to end in the Client's Stripe test environment (test mode), covering the checkout, webhook, and settlement paths against Stripe test cards. No funds, payment instruments, or spend cap are required.
Confirm the services hosted on the authorised external network address.
Nominate an emergency contact available during the testing window, and confirm any blackout periods or change windows to be avoided.
Provide a short product walkthrough so that testers understand the intended workflows before testing begins.
Confirm remediation status and submit findings for retesting through the CTB platform during the retesting period.`;

export const DependenciesAssumptionsEditor: React.FC<DependenciesAssumptionsEditorProps> = ({
  fields,
  setField
}) => {
  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <CheckSquare size={16} className="text-ctb-blue" />
          Section 6: Assumptions & Client Dependencies
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Specify technical preconditions, credentials requirements, allowlisting, and points of contact required from the Client prior to kick-off.
        </p>
      </div>

      {/* Assumptions */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <HelpCircle size={13} className="text-slate-500" />
            Engagement Assumptions
          </label>
          <button
            type="button"
            onClick={() => setField("assumptions", defaultAssumptions)}
            className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
          >
            <RotateCcw size={11} />
            Reset to standard
          </button>
        </div>
        <textarea
          rows={6}
          value={fields.assumptions ?? ""}
          onChange={(e) => setField("assumptions", e.target.value)}
          placeholder="Enter engagement assumptions..."
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
        />
        <span className="text-[10px] text-slate-400">
          Standard testing assumptions protecting scope and technical baseline.
        </span>
      </div>

      {/* Client Dependencies */}
      <div className="space-y-3 pt-2 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <AlertCircle size={13} className="text-amber-600" />
            Client Dependencies (Allowlisting, Accounts & Cooperation)
          </label>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setField("clientDependencies", defaultClientDependencies)}
              className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
            >
              <RotateCcw size={11} />
              Reset to standard
            </button>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={fields.clientDependenciesEnabled}
                onChange={(e) => setField("clientDependenciesEnabled", e.target.checked)}
                className="rounded border-slate-300 text-ctb-blue focus:ring-ctb-blue"
              />
              Include Section in SOW
            </label>
          </div>
        </div>

        <textarea
          rows={7}
          value={fields.clientDependencies ?? ""}
          onChange={(e) => setField("clientDependencies", e.target.value)}
          placeholder={"1. Provisioning of test accounts\n2. Testing source IP allowlisting\n3. Dedicated emergency technical contact"}
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
        />
        <span className="text-[10px] text-slate-400">
          Enter each dependency on a new line. They will render as clean bullet points in the SOW PDF.
        </span>
      </div>
    </div>
  );
};
